import axios from "axios";
import { BARRIERS_ACCESS_TOKEN } from "../../config";

// --- AXIOS INSTANCA ---
const apiClient = axios.create({
  baseURL: "https://barriers-944718659764.europe-west1.run.app",
  headers: {
    "X-API-Key": BARRIERS_ACCESS_TOKEN,
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error("API Error:", error);
    const statusCode = error.response?.status || 500;
    const errorData = error.response?.data || { error: error.message || "Unknown error occurred" };
    return Promise.reject({ statusCode, errorData });
  }
);


// ==========================================
// SYSTEM (Sistem)
// ==========================================

/**
 * Provera statusa servera i baze podataka. (Liveness probe)
 */
export const getHealth = async () => {
  return await apiClient.get("/health");
};


// ==========================================
// PLATFORM TICKETS (Tiketi za rampu)
// ==========================================

/**
 * Kreiranje, ažuriranje ili otkazivanje tiketa (Idempotent upsert).
 * @param {Object} ticketData Podaci o tiketu (mora sadržati token_value)
 */
export const upsertPlatformTicket = async (ticketData) => {
  return await apiClient.post("/api/v1/platform-tickets", ticketData);
};

/**
 * Masovno kreiranje/ažuriranje tiketa (do 500 odjednom).
 * @param {Array} tickets Niz objekata sa podacima o tiketima
 */
export const bulkUpsertPlatformTickets = async (tickets) => {
  return await apiClient.post("/api/v1/platform-tickets/bulk", { tickets });
};

/**
 * Pretraga tiketa po opsegu datuma (paginirano).
 * @param {Object} params Parametri: { from, to, status?, type?, pass_model?, token?, limit?, offset? }
 *  - from / to su OBAVEZNI (ISO 8601).
 * @returns {Promise<{total:number, limit:number, offset:number, count:number, items:Array}>}
 */
export const searchPlatformTickets = async (params) => {
  return await apiClient.get("/api/v1/platform-tickets/search", { params });
};

/**
 * Lista tiketa pozivaoca (bez obaveznog opsega datuma, paginirano).
 * Za bogatije filtere i ukupan broj rezultata koristiti searchPlatformTickets.
 * @param {Object} [params] Parametri: { status?, limit?, offset? }
 * @returns {Promise<Array>} Niz tiketa (najnoviji prvi)
 */
export const listPlatformTickets = async (params = {}) => {
  return await apiClient.get("/api/v1/platform-tickets", { params });
};

/**
 * Dohvatanje tiketa na osnovu tokena.
 * @param {string} tokenValue Opaque QR/RFID string
 */
export const getPlatformTicket = async (tokenValue) => {
  return await apiClient.get(`/api/v1/platform-tickets/${encodeURIComponent(tokenValue)}`);
};

/**
 * Kompletna istorija očitavanja (logovi) za jednu kartu / token.
 * Hvata sve varijante zapisa UID-a kartice (padovanu i kanonsku).
 * @param {string} tokenValue Opaque QR/RFID string
 * @param {Object} [params] Parametri: { result?: 'all'|'valid'|'denied', source?: 'all'|'online'|'offline', limit?, offset? }
 * @returns {Promise<{token_value:string, queried_variants:Array, ticket:Object|null, summary:Object, events:Array}>}
 */
export const getTicketEvents = async (tokenValue, params = {}) => {
  return await apiClient.get(
    `/api/v1/platform-tickets/${encodeURIComponent(tokenValue)}/events`,
    { params }
  );
};

/**
 * Brisanje tiketa na osnovu tokena.
 * @param {string} tokenValue Opaque QR/RFID string
 */
export const deletePlatformTicket = async (tokenValue) => {
  return await apiClient.delete(`/api/v1/platform-tickets/${encodeURIComponent(tokenValue)}`);
};


// ==========================================
// TERMINALS (Terminali / Rampe)
// ==========================================

/**
 * Dohvatanje liste terminala operatora.
 */
export const listTerminals = async () => {
  return await apiClient.get("/api/v1/terminals");
};

/**
 * Povezivanje terminala sa operatorom (Provisioning).
 * @param {string} cpuSerial Serijski broj terminala (npr. 'CPU-BG-1')
 */
export const bindTerminalToOperator = async (cpuSerial) => {
  return await apiClient.put(`/api/v1/terminals/${cpuSerial}/operator`);
};

/**
 * Izdavanje ili rotacija WebSocket ključa za specifični terminal.
 * NAPOMENA: API ključ se vraća samo jednom u odgovoru!
 * @param {string} cpuSerial Serijski broj terminala
 */
export const issueTerminalWsKey = async (cpuSerial) => {
  return await apiClient.post(`/api/v1/terminals/${cpuSerial}/ws-key`);
};

/**
 * Opoziv (brisanje) WebSocket ključa za terminal.
 * @param {string} cpuSerial Serijski broj terminala
 */
export const revokeTerminalWsKey = async (cpuSerial) => {
  return await apiClient.delete(`/api/v1/terminals/${cpuSerial}/ws-key`);
};

/**
 * Izvođenje determinističkog HMAC WebSocket tokena terminala (legacy auth putanja).
 * NAPOMENA: Preporučeno je koristiti issueTerminalWsKey (nasumičan, opoziv ključ).
 * Vraća { cpu_serial, ws_token, ws_url }.
 * @param {string} cpuSerial Serijski broj terminala
 */
export const deriveTerminalWsToken = async (cpuSerial) => {
  return await apiClient.post(`/api/v1/terminals/${cpuSerial}/ws-token`);
};

/**
 * Provera žive WebSocket konekcije terminala (da li UI sme da ponudi restart).
 * Vraća { cpu_serial, connected, sockets, last_seen_at, reboot_cooldown_remaining_ms }.
 * @param {string} cpuSerial Serijski broj terminala
 */
export const getTerminalConnection = async (cpuSerial) => {
  return await apiClient.get(`/api/v1/terminals/${cpuSerial}/connection`);
};

/**
 * Daljinski restart (reboot) terminala. Šalje Cominfo servisni frame
 * preko otvorene WebSocket konekcije. Terminal je van funkcije ~30-60 s.
 * NAPOMENA: Rampa neće raditi dok se uređaj ne podigne. Postoji cooldown
 * (podrazumevano 60 s) — ponovljeni zahtev u tom periodu vraća 429.
 * Bez žive konekcije komanda se ne može isporučiti (409).
 * Vraća 202 sa { cpu_serial, command, command_id, delivered_to, acked, requested_at, cooldown_ms }.
 * @param {string} cpuSerial Serijski broj terminala
 */
export const rebootTerminal = async (cpuSerial) => {
  return await apiClient.post(`/api/v1/terminals/${cpuSerial}/reboot`);
};


// ==========================================
// TERMINAL CONFIG (Param konfiguracija)
// ==========================================

/**
 * Dohvatanje param konfiguracije terminala (overrides + effective + param_stamp).
 * @param {string} cpuSerial Serijski broj terminala
 */
export const getTerminalParam = async (cpuSerial) => {
  return await apiClient.get(`/api/v1/terminals/${cpuSerial}/param`);
};

/**
 * Postavljanje param overrides-a za terminal. Svaki top-level ključ menja
 * odgovarajući ključ u podrazumevanom profilu.
 * @param {string} cpuSerial Serijski broj terminala
 * @param {Object} overrides Cominfo param ključevi (npr. { mode: "online" })
 */
export const setTerminalParam = async (cpuSerial, overrides) => {
  return await apiClient.put(`/api/v1/terminals/${cpuSerial}/param`, overrides);
};

/**
 * Vraćanje terminala na podrazumevani profil (brisanje overrides-a).
 * @param {string} cpuSerial Serijski broj terminala
 */
export const resetTerminalParam = async (cpuSerial) => {
  return await apiClient.delete(`/api/v1/terminals/${cpuSerial}/param`);
};


// ==========================================
// SELLERS (Prodavci / Preprodavci)
// ==========================================

/**
 * Kreiranje novog prodavca. (Automatski ga dodeljuje trenutnom operatoru).
 * @param {Object} [sellerData={}] Opciono: { name: "Ime prodavca" }
 */
export const createSeller = async (sellerData = {}) => {
  return await apiClient.post("/api/v1/sellers", sellerData);
};

/**
 * Dohvatanje liste prodavaca akreditovanih od strane ovog operatora.
 */
export const listSellers = async () => {
  return await apiClient.get("/api/v1/sellers");
};

/**
 * Dohvatanje specifičnog prodavca.
 * @param {number} sellerId ID prodavca
 */
export const getSeller = async (sellerId) => {
  return await apiClient.get(`/api/v1/sellers/${sellerId}`);
};

/**
 * Ažuriranje prodavca (ime ili status opoziva).
 * @param {number} sellerId ID prodavca
 * @param {Object} updates Podaci za ažuriranje (npr. { name: "Novo Ime", revoked: true })
 */
export const patchSeller = async (sellerId, updates) => {
  return await apiClient.patch(`/api/v1/sellers/${sellerId}`, updates);
};

/**
 * Davanje prava postojećem prodavcu da prodaje za ovog operatora.
 * @param {number} sellerId ID prodavca
 */
export const grantSellerToOperator = async (sellerId) => {
  return await apiClient.post(`/api/v1/sellers/${sellerId}/grants`);
};

/**
 * Rotacija API ključa prodavca.
 * NAPOMENA: Novi ključ se vraća samo jednom u odgovoru!
 * @param {number} sellerId ID prodavca
 */
export const rotateSellerKey = async (sellerId) => {
  return await apiClient.post(`/api/v1/sellers/${sellerId}/keys`);
};


// ==========================================
// REPORTS (Izveštaji)
// ==========================================

/**
 * Dohvatanje agregiranog izveštaja o prolazima.
 * @param {Object} params Parametri: { from: 'ISO_DATE', to: 'ISO_DATE', granularity?: 'hour'|'day', seller_id?: number }
 */
export const getPassesReport = async (params) => {
  return await apiClient.get("/api/v1/reports/passes", { params });
};


// ==========================================
// EVENTS (Pojedinačni prolazi / pokušaji)
// ==========================================

/**
 * Pretraga pojedinačnih prolaza (uključujući odbijene).
 * @param {Object} params Parametri: { from, to, result?, media?, source?, reason?, token?, cpu_serial?, limit?, offset? }
 *  - from / to su OBAVEZNI (ISO 8601).
 * @returns {Promise<{total:number, limit:number, offset:number, events:Array}>}
 */
export const searchEvents = async (params) => {
  return await apiClient.get("/api/v1/events", { params });
};

/**
 * Zbirni pregled razloga odbijanja, grupisano po razlogu i čitaču.
 * @param {Object} params Parametri: { from, to } (ISO 8601, oba obavezna)
 */
export const getDenyReasons = async (params) => {
  return await apiClient.get("/api/v1/events/deny-reasons", { params });
};